import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useClients, type Client, type ClientSort, type ClientsRange, type SortDir } from "../../hooks/useClients";
import { useClientProfile, type ClientInteraction } from "../../hooks/useClientProfile";
import { PlayersBadge } from "../../components/shared";
import type { DateRange } from "../../components/DateSelector";
import { fmtDateApi } from "../../utils/date";
import css from "./dcSections.module.css";

type TypeKey = "games" | "bookings" | "edits";
const TYPE_CHIPS: { key: TypeKey; label: string }[] = [
  { key: "games", label: "Игры" },
  { key: "bookings", label: "Брони" },
  { key: "edits", label: "Изменения" },
];

const SORT_CHIPS: { key: ClientSort; label: string }[] = [
  { key: "recent", label: "Недавние" },
  { key: "games", label: "Игры" },
  { key: "money", label: "Деньги" },
];

function fmtRu(d: Date): string {
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Копирование в буфер: Clipboard API в защищённом контексте, иначе фолбэк через textarea. */
async function copyText(text: string): Promise<boolean> {
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* фолбэк ниже */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed"; ta.style.opacity = "0"; ta.style.pointerEvents = "none";
    document.body.appendChild(ta); ta.focus(); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta); return ok;
  } catch { return false; }
}

/**
 * DC «Клиенты» (screen-08). Search + sort (recent/games/money, 2nd tap toggles
 * asc/desc) + interaction-type chips + «Без даты» toggle. Tapping a client opens
 * an inline history with short interaction cards; a card expands to the detailed
 * report. «Показать ещё» appends without reloading (scroll preserved).
 */
export function DcClientsSection({ range: headerRange }: { range?: DateRange }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<ClientSort>("recent");
  const [dir, setDir] = useState<SortDir>("desc");
  const [types, setTypes] = useState<Set<TypeKey>>(new Set());
  const [ignoreDate, setIgnoreDate] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  // Мультивыделение: id → телефон (фиксируем телефон при выделении, чтобы копировать
  // независимо от текущей фильтрации списка). selectMode = есть хоть один выделенный.
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  const [toast, setToast] = useState("");
  const toastTimer = useRef<number | null>(null);

  const rangeActive = !ignoreDate && !!headerRange?.start && !!headerRange?.end;
  const range: ClientsRange | null = rangeActive
    ? { from: fmtDateApi(headerRange!.start!), to: fmtDateApi(headerRange!.end!) }
    : null;
  const flt = useMemo(() => [...types].sort().join(","), [types]);

  const { clients, loading, loadingMore, error, hasMore, loadMore } = useClients(query, { sort, dir, range, flt });

  /* Tap active sort chip again → flip direction; new chip → reset to desc. */
  const pickSort = (k: ClientSort) => {
    if (k === sort) setDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSort(k); setDir("desc"); }
  };

  const toggleType = (k: TypeKey) =>
    setTypes((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  // Тип-фильтр теперь server-side (по всей базе) — список приходит уже отфильтрованным.
  const shown = clients;

  const selectMode = selected.size > 0;
  const flash = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 1700);
  };
  const copyOne = async (phone: string) => {
    const ok = await copyText(phone.trim());
    flash(ok ? "Номер скопирован" : "Не удалось скопировать");
  };
  const copySelected = async () => {
    const list = [...selected.values()].map((p) => p.trim()).filter(Boolean);
    const ok = await copyText(list.join("\n"));
    flash(ok ? `Скопировано номеров: ${list.length}` : "Не удалось скопировать");
  };
  // Тап по телефону: в обычном режиме — копируем один; при активном выделении —
  // все номера выделенных, каждый с новой строки.
  const onPhone = (phone: string) => { if (selectMode) void copySelected(); else void copyOne(phone); };
  const toggleSel = (cid: string, phone: string) =>
    setSelected((m) => { const n = new Map(m); n.has(cid) ? n.delete(cid) : n.set(cid, phone); return n; });
  const onActivate = (cid: string, phone: string) => {
    if (selectMode) toggleSel(cid, phone);
    else setOpenId((o) => (o === cid ? null : cid));
  };
  const onLongPress = (cid: string, phone: string) => {
    setSelected((m) => { const n = new Map(m); n.set(cid, phone); return n; });
    setOpenId(null);
  };

  const moneyMode = sort === "money";
  const rangeLabel = rangeActive && headerRange?.start && headerRange?.end
    ? `${fmtRu(headerRange.start)}–${fmtRu(headerRange.end)}`
    : null;
  const arrow = dir === "desc" ? "↓" : "↑";

  return (
    <div className={css.main}>
      <div className={css.search}>
        <svg className={css.lens} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" aria-hidden="true"><circle cx="6" cy="6" r="4" /><path d="M9 9l4 4" /></svg>
        <input
          type="text"
          className={css.searchInput}
          placeholder="Имя, телефон или @telegram…"
          value={query}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
        />
        {query ? (
          <button type="button" className={css.searchClr} aria-label="Очистить" onClick={() => setQuery("")}>
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" /></svg>
          </button>
        ) : null}
      </div>

      {/* Сортировка — повторный тап по активному меняет направление */}
      <div className={css.sortBar}>
        <span className={css.sortLbl}>Сорт.</span>
        {SORT_CHIPS.map((s) => (
          <button key={s.key} type="button" className={`${css.sortChip} ${sort === s.key ? css.sortChipOn : ""}`} onClick={() => pickSort(s.key)}>
            {s.label}{sort === s.key ? <span className={css.sortArrow}>{arrow}</span> : null}
          </button>
        ))}
      </div>

      <div className={css.typeChips}>
        {TYPE_CHIPS.map((c) => (
          <button key={c.key} type="button" className={`${css.tChip} ${types.has(c.key) ? css.tChipOn : ""}`} onClick={() => toggleType(c.key)}>
            {c.label}
          </button>
        ))}
        <button type="button" className={`${css.tChip} ${css.tChipDate} ${ignoreDate ? css.tChipOn : ""}`} onClick={() => setIgnoreDate((v) => !v)}>
          {ignoreDate ? "Без даты" : rangeLabel ?? "За период"}
        </button>
      </div>

      {selectMode ? (
        <div className={css.selBar}>
          <span className={css.selCount}>Выбрано <b>{selected.size}</b></span>
          <button type="button" className={css.selCopy} onClick={() => void copySelected()}>Скопировать номера</button>
          <button type="button" className={css.selClear} onClick={() => setSelected(new Map())}>Снять</button>
        </div>
      ) : null}

      <div className={css.listLbl}>
        {query ? "Результаты" : rangeActive ? "Клиенты за период" : "Последние клиенты"}
        {!loading && shown.length ? <b className={css.listCount}>{shown.length}</b> : null}
      </div>

      {error ? (
        <div className={css.err}>Ошибка: {error}</div>
      ) : loading ? (
        <div className={css.loading}>Загрузка…</div>
      ) : shown.length === 0 ? (
        <div className={css.empty}>{query || rangeActive || types.size ? "Ничего не найдено" : "Клиентов нет"}</div>
      ) : (
        <>
          {shown.map((c) => {
            const cid = String(c.id);
            return (
              <ClientRow
                key={cid}
                c={c}
                moneyMode={moneyMode}
                open={openId === cid && !selectMode}
                selected={selected.has(cid)}
                selectMode={selectMode}
                onActivate={(phone) => onActivate(cid, phone)}
                onLongPress={(phone) => onLongPress(cid, phone)}
                onPhone={onPhone}
              />
            );
          })}
          {hasMore && !query ? (
            <button type="button" className={css.showMore} onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Загрузка…" : "Показать ещё"}
            </button>
          ) : null}
        </>
      )}

      {toast ? <div className={css.toast} role="status">{toast}</div> : null}
    </div>
  );
}

function ClientRow({ c, moneyMode, open, selected, selectMode, onActivate, onLongPress, onPhone }: {
  c: Client; moneyMode: boolean; open: boolean; selected: boolean; selectMode: boolean;
  onActivate: (phone: string) => void; onLongPress: (phone: string) => void; onPhone: (phone: string) => void;
}) {
  const name = (c.name ?? "").trim() || "Без имени";
  const phone = (c.phone ?? "").trim();
  const visits = c.total_visits ?? 0;
  const spent = c.total_spent ?? 0;

  // Долгий тап (≈480мс без сдвига пальца) → выделение. Сдвиг = это скролл, отменяем.
  const timer = useRef<number | null>(null);
  const longFired = useRef(false);
  const start = useRef({ x: 0, y: 0 });
  const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const onPointerDown = (e: ReactPointerEvent) => {
    longFired.current = false;
    start.current = { x: e.clientX, y: e.clientY };
    clearTimer();
    timer.current = window.setTimeout(() => { longFired.current = true; onLongPress(phone); }, 480);
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    if (timer.current && (Math.abs(e.clientX - start.current.x) > 8 || Math.abs(e.clientY - start.current.y) > 8)) clearTimer();
  };
  const handleClick = () => { clearTimer(); if (longFired.current) { longFired.current = false; return; } onActivate(phone); };

  return (
    <div className={`${css.clCard} ${open ? css.clCardOpen : ""} ${selected ? css.clCardSel : ""}`}>
      <div
        className={`${css.clRow} ${selected ? css.clRowSel : ""}`}
        role="button"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerUp={clearTimer}
        onPointerLeave={clearTimer}
        onPointerMove={onPointerMove}
        onClick={handleClick}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className={css.clName}>{name}</div>
        <div className={css.clVisits}>
          {moneyMode ? (
            <><span className={css.clN}>{spent.toLocaleString("ru-RU")}</span><span className={css.clL}>₽</span></>
          ) : (
            <><span className={css.clN}>{visits}</span><span className={css.clL}>виз.</span></>
          )}
        </div>
        <div className={css.clMeta}>
          {phone ? (
            <button
              type="button"
              className={css.clPh}
              title={selectMode ? "Скопировать выделенные номера" : "Скопировать номер"}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onPhone(phone); }}
            >
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 3a1 1 0 0 1 1-1h1.5l1 2-1 1a6 6 0 0 0 3 3l1-1 2 1V9.5a1 1 0 0 1-1 1A9 9 0 0 1 2 3z" /></svg>
              {phone}
            </button>
          ) : null}
          {c.favorite_quest ? (
            <span className={css.clFav}>
              <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><path d="M6 11C2 8 1 6 1 4a2.5 2.5 0 0 1 5-1 2.5 2.5 0 0 1 5 1c0 2-1 4-5 7z" /></svg>
              {c.favorite_quest}
            </span>
          ) : null}
          {c.last_visit ? <span className={css.clLast}>{fmtDate(c.last_visit)}</span> : null}
          {selectMode ? (
            <span className={`${css.selCheck} ${selected ? css.selCheckOn : ""}`} aria-hidden="true">{selected ? "✓" : ""}</span>
          ) : (
            <span className={`${css.clChev} ${open ? css.clChevOpen : ""}`} aria-hidden="true">
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 5l3 3 3-3" /></svg>
            </span>
          )}
        </div>
      </div>
      {open ? <ClientHistory id={c.id} /> : null}
    </div>
  );
}

function ClientHistory({ id }: { id: string | number }) {
  const { profile, loading, error } = useClientProfile(id);
  if (loading) return <div className={css.ixLoading}>Загрузка истории…</div>;
  if (error) return <div className={css.err}>Ошибка: {error}</div>; if (!profile) return null;

  const phone = (profile.phone ?? "").trim();
  const tg = (profile.telegram ?? "").trim();
  const games = profile.games_played ?? 0; // игры = с отчётом; визиты ≠ игры (Б2)
  const spent = profile.total_spent ?? 0;
  const avg = games > 0 ? Math.round(Number(spent) / Number(games)) : 0;
  const history = profile.history ?? [];

  return (
    <div className={css.ixWrap}>
      <div className={css.ixProfile}>
        {phone ? <div className={css.ixpRow}><span className={css.ixpK}>Телефон</span><a className={css.ixpV} href={`tel:${phone.replace(/[^\d+]/g, "")}`}>{phone}</a></div> : null}
        {tg ? <div className={css.ixpRow}><span className={css.ixpK}>Telegram</span><span className={`${css.ixpV} ${css.ixpAccent}`}>{tg}</span></div> : null}
        <div className={css.ixpRow}><span className={css.ixpK}>Всего игр</span><span className={css.ixpV}><b>{games}</b></span></div>
        <div className={css.ixpRow}><span className={css.ixpK}>Сумма</span><span className={`${css.ixpV} ${css.ixpGold}`}>{Number(spent).toLocaleString("ru-RU")} ₽</span></div>
        {avg > 0 ? <div className={css.ixpRow}><span className={css.ixpK}>Средний чек</span><span className={`${css.ixpV} ${css.ixpGold}`}>{avg.toLocaleString("ru-RU")} ₽</span></div> : null}
        {profile.favorite_quest ? <div className={css.ixpRow}><span className={css.ixpK}>Любимый</span><span className={css.ixpV}>{profile.favorite_quest}</span></div> : null}
      </div>

      <div className={css.ixSecLbl}>Взаимодействия {history.length ? <b>{history.length}</b> : null}</div>
      {history.length === 0 ? (
        <div className={css.empty}>История пуста</div>
      ) : (
        history.map((ix) => <IxCard key={ix.id} ix={ix} />)
      )}
    </div>
  );
}

type IxType = "game" | "book" | "cancel";
function ixType(ix: ClientInteraction): IxType {
  if (ix.status === "cancelled") return "cancel";
  if (ix.report_total != null || ix.operator_id) return "game";
  return "book";
}
const IX_LABEL: Record<IxType, string> = { game: "Игра", book: "Бронь", cancel: "Отмена" };
const IX_CLS: Record<IxType, string> = { game: css.ixGame ?? "", book: css.ixBook ?? "", cancel: css.ixCancel ?? "" };

function IxCard({ ix }: { ix: ClientInteraction }) {
  const [open, setOpen] = useState(false);
  const t = ixType(ix);
  const money = ix.report_total ?? ix.prepaid_amount ?? 0;
  const hasDetail = t === "game" ? !!(ix.report_comment || ix.report_activity || ix.quest_price) : !!(ix.booking_comment || ix.prepaid_amount);
  const breakdown = [
    ix.quest_price ? `квест ${Number(ix.quest_price).toLocaleString("ru-RU")}₽` : "",
    ix.tea_price ? `чай ${Number(ix.tea_price).toLocaleString("ru-RU")}₽` : "",
    ix.mc_price ? `МК ${Number(ix.mc_price).toLocaleString("ru-RU")}₽` : "",
  ].filter(Boolean).join(" · ");

  return (
    <div className={`${css.ixCard} ${IX_CLS[t]}`} role="button" tabIndex={0} onClick={() => hasDetail && setOpen((o) => !o)}>
      <div className={css.ixHead}>
        <span className={css.ixType}>{IX_LABEL[t]}</span>
        <span className={css.ixWhen}>{fmtDate(ix.date)}{ix.time ? ` · ${String(ix.time).slice(0, 5)}` : ""}</span>
        {ix.short_name ? <span className={css.ixQuest}>{ix.short_name}</span> : null}
      </div>
      <div className={css.ixBody}>
        {/* 05.09 (владелец): стандартный бейдж; id истории = bookings.id (clients-profile.ts), лид — брони нет → индикатор */}
        {ix.players ? <PlayersBadge count={ix.players} bookingId={ix.type === "lead" ? undefined : ix.id} size="sm" /> : null}
        {Number(money) > 0 ? <span className={css.ixSum}>{Number(money).toLocaleString("ru-RU")}₽</span> : null}
        {ix.operator_name ? <span className={css.ixOp}>★ {ix.operator_name}</span> : null}
        {ix.source ? <span className={css.ixSrc}>{ix.source}</span> : null}
        {hasDetail ? <span className={`${css.ixChev} ${open ? css.clChevOpen : ""}`} aria-hidden="true">▾</span> : null}
      </div>
      {open ? (
        <div className={css.ixExpand}>
          {ix.report_comment ? <div className={css.ixRep}>{ix.report_comment}</div> : null}
          {ix.report_activity ? <div className={css.ixRepMeta}>Активность: <b>{ix.report_activity}</b></div> : null}
          {breakdown ? <div className={css.ixRepMeta}>{breakdown}</div> : null}
          {Number(ix.report_cash) > 0 ? <div className={css.ixRepMeta}>Наличными: <b>{Number(ix.report_cash).toLocaleString("ru-RU")}₽</b></div> : null}
          {Number(ix.prepaid_amount) > 0 ? <div className={css.ixRepMeta}>Предоплата: <b>{Number(ix.prepaid_amount).toLocaleString("ru-RU")}₽</b></div> : null}
          {ix.booking_comment ? <div className={css.ixRep}>{ix.booking_comment}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

function fmtDate(iso: string): string {
  const d = iso.slice(0, 10).split("-");
  return d.length === 3 ? `${d[2]}.${d[1]}.${d[0]}` : iso;
}
